import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import {
  FiArrowLeft, FiDollarSign, FiEdit2, FiHome, FiUser, FiCreditCard, FiSmartphone,
  FiInfo, FiCheckCircle,
} from 'react-icons/fi';
import AdminShell from '../../components/AdminShell/AdminShell';
import styles from './SystemSettings.module.css';
import axiosClient from '../../api/axiosClient';
import { useCurrency } from '../../app/CurrencyContext';
import FeatureSwitchesPanel from '../../components/FeatureSwitches/FeatureSwitchesPanel';

interface Settings {
  usdt_inr_rate: string; usdt_eur_rate: string; bct_price_usdt: string; usdt_withdrawal_rate: string;
  default_referral_code: string | null;
  withdrawal_admin_charge_pct: string;
}
interface ChargeLog { old_value: string | null; new_value: string; changed_at: string; changed_by_name: string | null }
interface BankAccount {
  currency: string;
  bank_name: string | null; account_holder: string | null;
  account_number: string | null; ifsc_code: string | null; upi_id: string | null;
}

const CURRENCY_META: Record<string, { label: string; icon: string }> = {
  INR: { label: 'Indian Rupee (INR)', icon: '₹' },
  USD: { label: 'US Dollar (USD)', icon: '$' },
  EUR: { label: 'Euro (EUR)', icon: '€' },
};

const SystemSettings: React.FC = () => {
  const { currencies: adminCurrencies } = useCurrency();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [banks, setBanks] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [rateInputs, setRateInputs] = useState<{ INR: string; EUR: string }>({ INR: '', EUR: '' });
  const [withdrawalRateInput, setWithdrawalRateInput] = useState('');
  const [bctInput, setBctInput] = useState('');
  const [chargeInput, setChargeInput] = useState('');
  const [chargeHistory, setChargeHistory] = useState<ChargeLog[]>([]);
  const [refCodeInput, setRefCodeInput] = useState('');
  const [savingRefCode, setSavingRefCode] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ bankName: '', accountHolder: '', accountNumber: '', ifscCode: '', upiId: '' });

  const load = () => {
    axiosClient.get('/admin/settings').then(({ data }) => {
      setSettings(data.settings);
      setBanks(data.bankAccounts);
      setRateInputs({ INR: data.settings.usdt_inr_rate, EUR: data.settings.usdt_eur_rate });
      setWithdrawalRateInput(data.settings.usdt_withdrawal_rate);
      setBctInput(data.settings.bct_price_usdt);
      setChargeInput(String(data.settings.withdrawal_admin_charge_pct ?? ''));
      setRefCodeInput(data.settings.default_referral_code || '');
    }).finally(() => setLoading(false));
    axiosClient.get('/admin/settings/withdrawal-charge/history')
      .then(({ data }) => setChargeHistory(data.history || []))
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const updateCharge = async () => {
    try {
      const { data } = await axiosClient.patch('/admin/settings/withdrawal-charge', { pct: chargeInput });
      toast.success(data.message || 'Withdrawal admin charge updated.');
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update the admin charge.');
    }
  };

  const updateRate = async (currency: 'INR' | 'EUR') => {
    try {
      await axiosClient.patch('/admin/settings/rate', { currency, rate: rateInputs[currency] });
      toast.success(`${currency} rate updated.`);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update rate.');
    }
  };

  const updateBct = async () => {
    try {
      await axiosClient.patch('/admin/settings/bct-price', { price: bctInput });
      toast.success('BCT price updated.');
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update price.');
    }
  };

  const updateReferralCode = async () => {
    setSavingRefCode(true);
    try {
      await axiosClient.patch('/admin/settings/referral-code', { code: refCodeInput });
      toast.success('Default referral code updated.');
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update referral code.');
    } finally {
      setSavingRefCode(false);
    }
  };

  const updateWithdrawalRate = async () => {
    try {
      await axiosClient.patch('/admin/settings/withdrawal-rate', { rate: withdrawalRateInput });
      toast.success('Withdrawal rate updated.');
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update withdrawal rate.');
    }
  };

  const openEdit = (bank: BankAccount) => {
    setEditingCurrency(bank.currency);
    setEditForm({
      bankName: bank.bank_name || '', accountHolder: bank.account_holder || '',
      accountNumber: bank.account_number || '', ifscCode: bank.ifsc_code || '', upiId: bank.upi_id || '',
    });
  };

  const saveBank = async () => {
    if (!editingCurrency) return;
    try {
      await axiosClient.put(`/admin/settings/bank-account/${editingCurrency}`, editForm);
      toast.success(`${editingCurrency} bank details saved.`);
      setEditingCurrency(null);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not save bank details.');
    }
  };

  if (loading || !settings) {
    return <AdminShell><div className={styles.page}><div className={styles.muted}>Loading…</div></div></AdminShell>;
  }

  return (
    <AdminShell>
      <div className={styles.page}>
        <Link to="/admin" className={styles.backLink}><FiArrowLeft /> Back</Link>

        <div className={styles.titleRow}>
          <div>
            <h1>System Settings</h1>
            <p>Manage USDT rates and bank account details by currency</p>
          </div>
          <span className={styles.adminBadge}><span className={styles.dot} /> Admin Access</span>
        </div>

        <div className={styles.rateGrid}>
          <div className={`${styles.rateCard} ${styles.ratePurple}`}>
            <div className={styles.rateTop}>
              <div className={styles.rateIcon}><FiDollarSign /></div>
              <div>
                <span>Current USDT to INR Rate</span>
                <b>₹{Number(settings.usdt_inr_rate).toFixed(2)} <small>/ USDT</small></b>
              </div>
              <button className={styles.updateBtn} onClick={() => updateRate('INR')}><FiEdit2 /> Update Rate</button>
            </div>
            <div className={styles.rateFoot}>
              1 USDT =
              <input value={rateInputs.INR} onChange={(e) => setRateInputs((v) => ({ ...v, INR: e.target.value }))} />
              INR
            </div>
          </div>

          <div className={`${styles.rateCard} ${styles.rateGreen}`}>
            <div className={styles.rateTop}>
              <div className={styles.rateIcon}>€</div>
              <div>
                <span>Current USDT to EUR Rate</span>
                <b>€{Number(settings.usdt_eur_rate).toFixed(2)} <small>/ USDT</small></b>
              </div>
              <button className={styles.updateBtn} onClick={() => updateRate('EUR')}><FiEdit2 /> Update Rate</button>
            </div>
            <div className={styles.rateFoot}>
              1 USDT =
              <input value={rateInputs.EUR} onChange={(e) => setRateInputs((v) => ({ ...v, EUR: e.target.value }))} />
              EUR
            </div>
          </div>

          <div className={`${styles.rateCard} ${styles.rateOrange}`}>
            <div className={styles.rateTop}>
              <div className={styles.rateIcon}><FiDollarSign /></div>
              <div>
                <span>Crypto Withdrawal Rate (daily)</span>
                <b>₹{Number(settings.usdt_withdrawal_rate).toFixed(2)} <small>/ USDT</small></b>
              </div>
              <button className={styles.updateBtn} onClick={updateWithdrawalRate}><FiEdit2 /> Update Rate</button>
            </div>
            <div className={styles.rateFoot}>
              1 USDT =
              <input value={withdrawalRateInput} onChange={(e) => setWithdrawalRateInput(e.target.value)} />
              INR — update this daily per live market price
            </div>
          </div>

          <div className={`${styles.rateCard} ${styles.rateOrange}`}>
            <div className={styles.rateTop}>
              <div className={styles.rateIcon}><FiDollarSign /></div>
              <div>
                <span>Withdrawal Admin Charge</span>
                <b>{Number(settings.withdrawal_admin_charge_pct).toFixed(2)}% <small>of each withdrawal</small></b>
              </div>
              <button className={styles.updateBtn} onClick={updateCharge}><FiEdit2 /> Update Charge</button>
            </div>
            <div className={styles.rateFoot}>
              Charge =
              <input value={chargeInput} onChange={(e) => setChargeInput(e.target.value)} />
              % — applies to new withdrawal requests only (pending ones keep their saved charge)
            </div>
            {chargeHistory.length > 0 && (
              <div className={styles.rateFoot} style={{ display: 'block', marginTop: 8, fontSize: 11.5 }}>
                <b>Recent changes:</b>
                {chargeHistory.slice(0, 5).map((h, i) => (
                  <div key={i}>
                    {h.old_value ?? '—'}% → {h.new_value}% · {h.changed_by_name || 'admin'} · {new Date(h.changed_at).toLocaleString()}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}><FiInfo /></div>
          <div>
            <h2>Emergency switches (all users)</h2>
            <p>Pause a feature for every user at once. Per-user switches are on each user's profile page.</p>
          </div>
        </div>
        <div className={styles.bankCard} style={{ marginBottom: 24 }}>
          <FeatureSwitchesPanel mode="platform" />
        </div>

        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}><FiHome /></div>
          <div>
            <h2>Bank Account Details by Currency</h2>
            <p>Configure bank accounts for your supported currencies ({adminCurrencies.map((c) => c.code).join(', ') || '—'})</p>
          </div>
        </div>

        <div className={styles.bankGrid}>
          {banks.map((bank) => {
            const currencyInfo = adminCurrencies.find((c) => c.code === bank.currency);
            const meta = currencyInfo
              ? { label: `${currencyInfo.name} (${currencyInfo.code})`, icon: currencyInfo.symbol }
              : CURRENCY_META[bank.currency] || { label: bank.currency, icon: bank.currency };
            const hasDetails = bank.bank_name || bank.account_number || bank.upi_id;
            return (
              <div key={bank.currency} className={styles.bankCard}>
                <div className={styles.bankCardHeader}>
                  <div className={styles.bankIcon}>{meta.icon}</div>
                  <div>
                    <b>{meta.label}</b>
                    <span>Bank Account Details</span>
                  </div>
                  <button className={styles.editIconBtn} onClick={() => openEdit(bank)}><FiEdit2 /></button>
                </div>
                {hasDetails ? (
                  <div className={styles.bankDetails}>
                    <div><FiHome /> <span>Bank:</span> <b>{bank.bank_name || '—'}</b></div>
                    <div><FiUser /> <span>Holder:</span> <b>{bank.account_holder || '—'}</b></div>
                    <div><FiCreditCard /> <span>Account:</span> <b>{bank.account_number ? `••••${bank.account_number.slice(-4)}` : '—'}</b></div>
                    <div><FiHome /> <span>IFSC:</span> <b>{bank.ifsc_code || '—'}</b></div>
                    <div><FiSmartphone /> <span>UPI:</span> <b>{bank.upi_id || '—'}</b></div>
                  </div>
                ) : (
                  <div className={styles.bankEmpty}>
                    <p>No bank details configured</p>
                    <span>Click edit to add bank details</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className={styles.infoBox}>
          <FiInfo />
          <p>
            These bank details will be shown to users based on their selected withdrawal currency.
            Please ensure all information is accurate before saving. The USDT prices affect all
            cryptocurrency to fiat conversions in the system.
          </p>
        </div>

        <div className={styles.sectionHeader}>
          <div className={styles.sectionIconOrange}>₿</div>
          <div>
            <h2>BCT to USDT Price Configuration</h2>
            <p>Update the current BCT price in USDT for smart contract interactions</p>
          </div>
        </div>

        <div className={styles.bctCard}>
          <span className={styles.bctLabel}>CURRENT BCT PRICE</span>
          <div className={styles.bctPrice}>{Number(settings.bct_price_usdt).toFixed(2)} <small>USDT</small></div>

          <label className={styles.bctInputLabel}>Set New Price</label>
          <div className={styles.bctInputRow}>
            <div className={styles.bctInputWrap}>
              <span>$</span>
              <input type="number" step="0.01" value={bctInput} onChange={(e) => setBctInput(e.target.value)} />
            </div>
            <button className={styles.bctUpdateBtn} onClick={updateBct}>Update</button>
          </div>

          <button className={styles.walletBtn}><FiCheckCircle /> Connect Wallet</button>
        </div>

        <div className={styles.sectionHeader}>
          <div className={styles.sectionIconOrange}>#</div>
          <div>
            <h2>Default Referral Code</h2>
            <p>Used by anyone registering without a personal referral link — from Register or the landing page's investor form. Must not match any user's own referral code.</p>
          </div>
        </div>

        <div className={styles.bctCard}>
          <span className={styles.bctLabel}>CURRENT DEFAULT CODE</span>
          <div className={styles.bctPrice}>{settings.default_referral_code || '—'}</div>

          <label className={styles.bctInputLabel}>Set New Code</label>
          <div className={styles.bctInputRow}>
            <div className={styles.bctInputWrap}>
              <span>#</span>
              <input
                value={refCodeInput}
                onChange={(e) => setRefCodeInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                placeholder="e.g. BLOCKCOST"
              />
            </div>
            <button className={styles.bctUpdateBtn} onClick={updateReferralCode} disabled={savingRefCode}>
              {savingRefCode ? 'Saving…' : 'Update'}
            </button>
          </div>
        </div>

        {editingCurrency && (
          <div className={styles.overlay} onClick={() => setEditingCurrency(null)}>
            <div className={styles.editModal} onClick={(e) => e.stopPropagation()}>
              <h3>Edit {editingCurrency} Bank Details</h3>
              <label>Bank Name</label>
              <input value={editForm.bankName} onChange={(e) => setEditForm((v) => ({ ...v, bankName: e.target.value }))} />
              <label>Account Holder</label>
              <input value={editForm.accountHolder} onChange={(e) => setEditForm((v) => ({ ...v, accountHolder: e.target.value }))} />
              <label>Account Number</label>
              <input value={editForm.accountNumber} onChange={(e) => setEditForm((v) => ({ ...v, accountNumber: e.target.value }))} />
              <label>IFSC Code</label>
              <input value={editForm.ifscCode} onChange={(e) => setEditForm((v) => ({ ...v, ifscCode: e.target.value }))} />
              <label>UPI ID</label>
              <input value={editForm.upiId} onChange={(e) => setEditForm((v) => ({ ...v, upiId: e.target.value }))} />
              <div className={styles.editActions}>
                <button className={styles.cancelBtn} onClick={() => setEditingCurrency(null)}>Cancel</button>
                <button className={styles.saveBtn} onClick={saveBank}>Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
};

export default SystemSettings;